import { Button, Divider, Form, Input, Modal, Select, Space, TableProps, Tabs, Typography }from "antd"
import { useCallback, useEffect, useRef, useState } from "react"
import { EyeOutlined, LeftCircleOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons'
import { filterOptionByLabel } from '../../utils/filter-utils'
import defaultParams from "../../assets/constants/default_params"
import { apiGetListCallInfoDetail, apiGetListCompanies, apiGetListPhoneAgencies, apiGetListTransactionConnection, apiUpdateListSimTransactionConnection, apiUpdatePhoneNumberTransactionConnection } from "../../api"
import { ICompany } from "../../models/company.modal"
import { formatCurrency } from "../../utils/string-utils"
import { renderDateByUnix, renderType } from "./method"
import { useSelector } from "react-redux"
import { authSelector } from "../../redux/slices/authSlice"
import CustomTable from "../../components/table"
import { useLoading } from "../../context/LoadingContext"
import { toastError, toastSuccess } from "../../utils/toast"
import { renderResult } from "../../helper/columns_helper"

const { Text } = Typography

export default function CustomerHistory() {
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [formSearch] = Form.useForm()
    const [transactionConnection, setTransactionConnection] = useState<any>({})
    const [agencies, setAgencies] = useState<any>([])
    const [search, setSearch] = useState<any>({})
    const [tabTransaction, setTabTransaction] = useState<any>({})
    const [modalUpdateListSim, setModalUpdateListSim] = useState<any>({ isOpen: false, data: null })
    const [updateListSimValue, setUpdateListSimValue] = useState<any>({ phoneSim: '', serialSim: '' })
    const [modalUpdatePhoneNumber, setModalUpdatePhoneNumber] = useState<any>({ isOpen: false, data: null })
    const [updatePhoneNumberValue, setUpdatePhoneNumberValue] = useState<any>({ phoneAgency: '', phoneCustomer: '' })
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const { account } = useSelector(authSelector)
    const { setIsLoading } = useLoading()
    const scrollRef = useRef(0)
    

    const transactionConnectionColumns: TableProps<any>['columns'] = [
        {
            key: 'cpCode',
            title: 'Mã công ty',
            dataIndex: 'cpCode',
        },
        {
            key: 'ownerBy',
            title: 'Nhân sự đấu nối',
            dataIndex: 'ownerBy',
        },
        {
            key: 'phoneAgency',
            title: 'SĐT báo KH',
            dataIndex: 'phoneAgency',
            render: phoneAgency => phoneAgency ? agencies.find((agen: any) => agen.id === phoneAgency)?.phoneAgency : '',
        },
        {
            key: 'phoneSim',
            title: 'Msisdn',
            dataIndex: 'phoneSim',
        },
        {
            key: 'serialSim',
            title: 'Serial Sim',
            dataIndex: 'serialSim',
        },
        {
            key: 'phoneCustomer',
            title: 'SĐT Khách hàng',
            dataIndex: 'phoneCustomer',
        },
        {
            key: 'dataPack',
            title: 'Gói cước',
            dataIndex: 'dataPack',
        },
        {
            key: 'price',
            title: 'Giá',
            dataIndex: 'price',
            render: price => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {formatCurrency(price)}
                </span>
            ),
        },
        {
            key: 'startDate',
            title: 'Ngày đấu nối',
            dataIndex: 'startDate',
            render: startDate => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {renderDateByUnix(startDate)}
                </span>
            ),
        },
        {
            key: 'endDate',
            title: 'Ngày hết hạn',
            dataIndex: 'endDate',
            render: endDate => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {renderDateByUnix(endDate)}
                </span>
            ),
        },
        {
            key: 'type',
            title: 'Loại gói cước',
            dataIndex: 'type',
            render: type => renderType(type),
        },
        (
            account?.canGetListCallInfoDetail
        ) ? {
            key: 'action',
            title: 'Tác vụ',
            render: data => renderAction(data)
        } : {
            key: 'action',
            title: 'Tác vụ',
            render: () => <span style={{ color: 'gray' }}>Không có quyền thao tác</span>
        },
        (
            account?.canUpdateListSimTransactionConnection
            || account?.canUpdatePhoneNumberTransactionConnection
            || account?.canUpdateDataPackTransactionConnection
        ) ? {
            key: 'update',
            title: 'Cập nhật',
            render: data => (
                <Space>
                    {account?.canUpdateListSimTransactionConnection && (
                        <Button size='small' onClick={() => {
                            setUpdateListSimValue({ phoneSim: data.phoneSim, serialSim: data.serialSim })
                            setModalUpdateListSim({ isOpen: true, data })
                        }}>
                            Sim
                        </Button>
                    )}
                    {account?.canUpdatePhoneNumberTransactionConnection && (
                        <Button size='small' onClick={() => {
                            setUpdatePhoneNumberValue({
                                phoneCustomer: data.phoneCustomer,
                            })
                            setModalUpdatePhoneNumber({ isOpen: true, data })
                        }}>
                            SĐT
                        </Button>
                    )}
                    
                </Space>
            ),
        } : {
            key: 'update',
            title: 'Cập nhật',
            render: () => <span style={{ color: 'gray' }}>Không có quyền thao tác</span>
        },
    ]

    const callInfoColumns: TableProps<any>['columns'] = [
        {
            key: 'owner_by',
            title: 'Người gọi',
            dataIndex: 'owner_by',
            render: owner_by => owner_by || 'system'
        },
        {
            key: 'result',
            title: 'Ghi chú',
            dataIndex: 'result',
            render: result => renderResult(result)
        },
        {
            key: 'sk',
            title: 'Thời gian',
            dataIndex: 'sk',
            render: sk => sk.split('#')[1],
        },
    ]
    
    const fetchPhoneAgencies = useCallback(async (trans: any) => {
        const idAgencies = [] as string[]
        trans.map((tran: any) => {
            if (tran.phoneAgency && !idAgencies.includes(tran.phoneAgency)) {
                idAgencies.push(`'${tran.phoneAgency}'`)
            }
            return tran
        })
        if (idAgencies.length > 0) {
            const respAgency = await apiGetListPhoneAgencies({
                page: 1,
                size: idAgencies.length,
                condition: `"cpCode" = '${search.cpCode}' AND  id IN (${idAgencies.toString()})`
            })
            setAgencies(respAgency.data.phoneAgencies)
        }
    }, [search.cpCode])

    const fetchTransactionConnection = useCallback(async () => {
        if (!search.cpCode && !search.serialSim ) {
            setTransactionConnection({})
            return
        }
        try {
            setIsLoading(true)
            const resp = await apiGetListTransactionConnection({
                page: currentPage,
                size: pageSize,
                ...search,
            })
            if (resp.status === 200) {
                fetchPhoneAgencies(resp.data.transactions)
                setTransactionConnection(resp.data)
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, fetchPhoneAgencies, currentPage, pageSize, search])


    const fetchCompanies = useCallback(async () => {
        try {
            const resp = await apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
            if (resp.data != null) {
                const options = resp.data.companies.map((item: ICompany) => ({
                    label: item.name,
                    value: item.code,
                }))
                setCompanyOptions(options)
            } else {
                setCompanyOptions([])
            }
        } catch (error) {
            console.log(error)
        }
    }, [])

    const fetchCallDetail = async (pk: string, cpCode: string) => {
        try {
            const resp = await apiGetListCallInfoDetail({ pk, cpCode })
            if (resp.status === 200) {
                return resp.data
            }
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        fetchTransactionConnection()
        fetchCompanies()
    }, [fetchTransactionConnection, fetchCompanies])

    const handleShowDetail = async ({ data, pk, cpCode }: any) => {
        const callDT = await fetchCallDetail(pk, cpCode)
        setTabTransaction({
            data: { ...data, pk, cpCode },
            call: callDT
        })
    }

    const handleSearch = ({ cpCode, serialSim}: any) => {
        if (!cpCode && !serialSim ) return
        
        setSearch({ cpCode, serialSim })
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        setSearch({})
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
    }

    const handleUpdateListSim = async () => {
        try {
            if (!updateListSimValue.phoneSim || !updateListSimValue.serialSim) {
                toastError('Vui lòng điền đầy đủ thông tin!')
                return
            }
            setIsLoading(true)
            const { data } = await apiUpdateListSimTransactionConnection({
                id: modalUpdateListSim.data.id,
                phoneSim: updateListSimValue.phoneSim,
                serialSim: updateListSimValue.serialSim,
            })
            if (data.status !== '200') {
                toastError(data.message)
                return
            }
            fetchTransactionConnection()
            setModalUpdateListSim({ isOpen: false, data: null })
            toastSuccess('Cập nhật thông tin thành công!')
        } catch (error) {
            toastError('Có lỗi xảy ra!')
            console.log(error);
        } finally {
            setIsLoading(false)
        }
    }

    const handleUpdatePhoneNumber = async () => {
        try {
            if (!updatePhoneNumberValue.phoneAgency && !updatePhoneNumberValue.phoneCustomer) {
                toastError('Vui lòng điền đầy đủ thông tin!')
                return
            }
            setIsLoading(true)
            const { data } = await apiUpdatePhoneNumberTransactionConnection({
                id: modalUpdatePhoneNumber.data.id,
                phoneCustomer: updatePhoneNumberValue.phoneCustomer,
            })
            if (data.status !== '200') {
                toastError(data.message)
                return
            }
            fetchTransactionConnection()
            setModalUpdatePhoneNumber({ isOpen: false, data: null })
            toastSuccess('Cập nhật thông tin thành công!')
        } catch (error) {
            toastError('Có lỗi xảy ra!')
            console.log(error);
        } finally {
            setIsLoading(false)
        }
    }

    const renderAction = (data: any) => {
        const pk = `${data.phoneSim}#${data.serialSim}`
        const cpCode = `${data.cpCode}`
        
        return (
            <Button onClick={() => {
                handleShowDetail({ data, pk, cpCode })
                scrollRef.current = window.scrollY
            }}
                style={{ width: 132, background: '#289e45', color: 'white' }}>
                Chi tiết <EyeOutlined />
            </Button>
        )
    }

    const renderCustomerHistory = () => {
        return (
            <>
                <Form form={formSearch} onFinish={handleSearch}>
                    <div style={{ display: 'flex', alignItems: 'start', flexWrap: 'wrap', gap: 20 }}>
                        <Form.Item name='cpCode' label='Công ty'
                            rules={[{ required: true, message: 'Vui lòng chọn công ty!' }]}>

                            <Select
                                style={{ minWidth: 170 }}
                                showSearch
                                placeholder="Chọn công ty"
                                options={[
                                    ...companyOptions,
                                    ]}
                                filterOption={filterOptionByLabel}
                            />
                        </Form.Item>
                        <Form.Item name='serialSim' label='SerialSim'
                            rules={[{ required: true, message: 'Vui lòng nhập serial sim!' }]}>
                            <Input autoComplete='off' />
                        </Form.Item>
                        <Space style={{ marginBottom: 24 }}>
                            <Button type='primary' htmlType="submit" icon={<SearchOutlined />}>Tìm kiếm</Button>
                            <Button onClick={handleClearSearch} icon={<UndoOutlined />}></Button>
                        </Space>
                    </div>
                </Form>

                <CustomTable
                    namePage='giao dịch'
                    columns={transactionConnectionColumns}
                    dataSource={transactionConnection.transactions}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    total={transactionConnection.total}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                />
            </>
        )
    }

    const renderDetail = (tran: any, call: any) => {
        return (<>
            <Button onClick={() => setTabTransaction({})}
                icon={<LeftCircleOutlined />}
                style={{ background: 'gray', color: 'white' }}>
                Trở lại
            </Button>
            <h2>Thông tin chi tiết ({tran.phoneCustomer || tran.phoneAgency})</h2>
            {renderDetailTransaction(tran)}
            <Tabs
                size='middle'
                items={[
                    {
                        key: 'call',
                        label: 'Cuộc gọi',
                        children: <div>
                            <CustomTable
                                namePage='cuộc gọi'
                                columns={callInfoColumns}
                                dataSource={call?.calls}
                                total={call?.total}
                                isHiddenPagination={true}
                            />
                        </div>,
                    },
                ]}
            />
        </>)
    }

    const renderDetailTransaction = (data: any) => {
        return (<>
            <h3 style={{ margin: 0 }}>Chi tiết giao dịch</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                <div style={{ flex: 1 }}>
                    <p><Text strong>Mã công ty </Text>{data?.cpCode}</p>
                    <p><Text strong>Msisdn: </Text>{data?.phoneSim}</p>
                    <p><Text strong>Gói cước: </Text>{data?.dataPack}</p>
                    <p><Text strong>Hạn sử dụng: </Text>{data?.numberDay}</p>
                    <p><Text strong>SĐT báo KH: </Text>{data?.phoneAgency}</p>
                    <p><Text strong>Ngày đấu nối: </Text>{renderDateByUnix(data?.startDate)}</p>
                </div>
                <div style={{ flex: 1 }}>
                    <p><Text strong>Nhân sự đấu: </Text>{data?.ownerBy}</p>
                    <p><Text strong>SerialSim: </Text>{data?.serialSim}</p>
                    <p><Text strong>Loại: </Text>{renderType(data?.type)}</p>
                    <p><Text strong>Giá bán: </Text>{formatCurrency(data?.price)}</p>
                    <p><Text strong>SĐT Khách hàng: </Text>{data?.phoneCustomer}</p>
                    <p><Text strong>Ngày hết hạn: </Text>{renderDateByUnix(data?.endDate)}</p>
                </div>
            </div>
        </>)
    }

    return (
        <div>
            {tabTransaction.data ? renderDetail(tabTransaction.data, tabTransaction.call) : renderCustomerHistory()}
            <Modal
                title='Cập nhật thông tin sim'
                open={modalUpdateListSim.isOpen}
                footer={null}
                onCancel={() => setModalUpdateListSim({ isOpen: false, data: null })}>
                <Divider />
                <div style={{ marginBottom: 20 }}>
                    <div style={{ marginBottom: 10 }}>Msisdn</div>
                    <Input
                        value={updateListSimValue.phoneSim}
                        onChange={e => setUpdateListSimValue({ ...updateListSimValue, phoneSim: e.target.value })}
                    />
                </div>
                <div style={{ marginBottom: 20 }}>
                    <div style={{ marginBottom: 10 }}>Serial Sim</div>
                    <Input
                        value={updateListSimValue.serialSim}
                        onChange={e => setUpdateListSimValue({ ...updateListSimValue, serialSim: e.target.value })}
                    />
                </div>
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={handleUpdateListSim} type='primary' htmlType='submit'>Cập nhật</Button>
                </div>
            </Modal>
            <Modal
                title='Cập nhật thông tin số điện thoại'
                open={modalUpdatePhoneNumber.isOpen}
                footer={null}
                onCancel={() => setModalUpdatePhoneNumber({ isOpen: false, data: null })}>
                <Divider />
                <div style={{ marginBottom: 20 }}>
                    <div style={{ marginBottom: 10 }}>SĐT Đại lý</div>
                    <Input
                        value={updatePhoneNumberValue.phoneAgency}
                        onChange={e => setUpdatePhoneNumberValue({ ...updatePhoneNumberValue, phoneAgency: e.target.value })}
                    />
                </div>
                <div style={{ marginBottom: 20 }}>
                    <div style={{ marginBottom: 10 }}>SĐT Khách hàng</div>
                    <Input
                        value={updatePhoneNumberValue.phoneCustomer}
                        onChange={e => setUpdatePhoneNumberValue({ ...updatePhoneNumberValue, phoneCustomer: e.target.value })}
                    />
                </div>
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={handleUpdatePhoneNumber} type='primary' htmlType='submit'>Cập nhật</Button>
                </div>
            </Modal>
        </div>
    )
}